import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';

export const EventCard = ({ event }) => {
  const { id, title, type, start_date, end_date, location_text, status, participant_count, cover_image } = event;
  const dateLabel = formatDateRange(start_date, end_date);
  const typeLabel = TYPE_LABELS[type] || 'Event';
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}>
      <Link to={`/events/${id}`} data-testid={`event-card-${id}`} className="block">
        <Card className="overflow-hidden rounded-2xl border bg-card shadow-[0_10px_30px_-18px_rgba(15,23,42,0.18)] hover:shadow-[0_18px_50px_-22px_rgba(15,23,42,0.35)] transition-shadow">
          <div className="relative h-32 sm:h-36 bg-secondary">
            {cover_image ? (
              <img src={cover_image} alt="" className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <div className="absolute inset-0 gradient-signature opacity-90" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent" />
            <div className="absolute top-3 left-3 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-white/90 bg-black/35 backdrop-blur px-2 py-1 rounded-full">{typeLabel}</span>
            </div>
            <div className="absolute top-3 right-3">
              <StatusChip status={status} />
            </div>
            <div className="absolute bottom-3 left-3 right-3">
              <h3 className="text-white font-serif text-xl font-semibold leading-tight line-clamp-2">{title}</h3>
            </div>
          </div>
          <div className="p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span className="truncate">{dateLabel}</span>
              </div>
              {location_text && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" />
                  <span className="truncate">{location_text}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
              <Users className="h-4 w-4" />
              <span>{participant_count ?? 0}</span>
            </div>
          </div>
        </Card>
      </Link>
    </motion.div>
  );
};

export const StatusChip = ({ status }) => {
  const map = {
    upcoming: { cls: 'bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border-[hsl(var(--accent))]/30', text: 'Upcoming' },
    ongoing: { cls: 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30', text: 'Ongoing' },
    past: { cls: 'bg-secondary text-secondary-foreground border-border', text: 'Past' },
  };
  const m = map[status] || map.upcoming;
  return <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${m.cls}`}>{m.text}</Badge>;
};

export const TYPE_LABELS = {
  road_trip: 'Road Trip',
  vacation: 'Vacation',
  conference: 'Conference',
  social: 'Social',
  custom: 'Custom',
};

function formatDateRange(s, e) {
  try {
    const sd = new Date(s);
    const ed = new Date(e);
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    if (sd.getFullYear() === ed.getFullYear() && sd.getMonth() === ed.getMonth() && sd.getDate() === ed.getDate()) {
      return sd.toLocaleDateString(undefined, opts);
    }
    return `${sd.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${ed.toLocaleDateString(undefined, opts)}`;
  } catch (err) {
    return '';
  }
}
